import { fromJS } from 'immutable';
import request from '../request';
import rentalTicket from '../../services/gma/rentalTicket';
import { GMI, STATE } from '../../constants';
import { types } from '../../utils/js/types';
import { formatTimeStamp } from '../../utils/app/reservations';

export const { INITIATE, DATE_TIME } = STATE.UI.RENTAL_TICKET;

/**
 * @private
 * @desc Cleaner for ui.rentalTicket.initiate.date_time
 * @param {String} part
 * @returns {Function}
 */
function clearRentalTicketInitiateDateTimeAction(part) {
  return (dispatch, getState) => {
    const {ts, isNewTs} = formatTimeStamp({state: getState(), part, path: DATE_TIME});

    if (ts && isNewTs) {
      // If only the T is left in timestamp, delete from state, else update based on new value
      if (ts === 'T') {
        dispatch({type: GMI.DELETE_GMI_STATE, path: DATE_TIME});
      } else {
        dispatch({type: GMI.SET_GMI_STATE, path: DATE_TIME, value: ts});
      }
    }
  };
}

/**
 * @private
 * @returns {Object} {type: string, path: [], value: object}
 */
function clearRentalTicket() {
  return {type: GMI.SET_GMI_STATE, path: INITIATE, value: {}};
}

/**
 * @private
 * @returns {Object} {type: string, path: [], value: object}
 */
function clearRentalTicketDate() {
  return dispatch => dispatch(clearRentalTicketInitiateDateTimeAction('date'));
}

/**
 * @private
 * @returns {Object} {type: string, path: [], value: object}
 */
function clearRentalTicketTime() {
  return dispatch => dispatch(clearRentalTicketInitiateDateTimeAction('time'));
}

/**
 * @private
 * @desc DRY, Function is used to simplify setRentalTicketDate and setRentalTicketTime
 * @param {Object} opts {timeStamp, part}
 * @returns {Function}
 */
function batchResInitiateDateOrTime(opts) {
  const {timeStamp, part} = opts;
  return (dispatch, getState) => {
    const value = formatTimeStamp({ts:timeStamp, state: getState(), part, path: DATE_TIME }).ts;
    dispatch({type: GMI.SET_GMI_STATE, path: DATE_TIME, value});
  };
}

/**
 * @private
 * @param {Object} value
 * @returns {Object} {type: string, path: [], value: object}
 */
function setRentalTicket(value) {
  return {type: GMI.SET_GMI_STATE, path: INITIATE, value};
}

/**
 * @private
 * @name setRentalTicketDate
 * @desc Set renral ticket initiate timestamp by date part
 * @param {String} timeStamp
 * @returns {Function}
 */
function setRentalTicketDate(timeStamp) {
  return dispatch => dispatch(batchResInitiateDateOrTime({ timeStamp, part: 'date' }));
}

/**
 * @private
 * @name setRentalTicketTime
 * @desc Set rental ticket initiate timestamp by time part
 * @param {String} timeStamp
 * @returns {Function}
 */
function setRentalTicketTime(timeStamp) {
  return dispatch => dispatch(batchResInitiateDateOrTime({ timeStamp, part: 'time' }));
}

/**
 * @private
 * @name setUiRentalTicket
 * @param {Object} [opts] { rentalState: Object }
 * @desc A setter for all rental ticket payload data
 * @returns {Function}
 */
function setUiRentalTicket(opts = {}) {
  return dispatch => dispatch(rentalTicketState.set(null, opts.rentalState));
}

export const rentalTicketState = {
  /**
   * @name set
   * @desc A global setter for ui.rentalTicket data
   * @param {String} [type]
   * @param {Object} [args]
   * @returns {Function}
   * @memberof actions.rentalTicket
   * @example
   * 
   * // Set RentalTicket
   * store.dispatch(actions.rentalTicket.rentalTicketState.set(null, {}));
   * 
   * // Set RentalTicketDate
   * store.dispatch(actions.rentalTicket.rentalTicketState.set('date', {}));
   * 
   * // Set RentalTicketTime
   * store.dispatch(actions.rentalTicket.rentalTicketState.set('time', {}));
   * 
   */
  set(type = null, args = {}) {
    return dispatch => {
      if (types(type).isString) {
        if (type === 'date') {
          dispatch(setRentalTicketDate(args.timestamp));
        } else if (type === 'time') {
          dispatch(setRentalTicketTime(args.timestamp));
        }
      } else {
        dispatch(setRentalTicket(args));
      }
    };
  },
  
  /**
   * @name unset
   * @desc A global unsetter for rentalTicket data
   * @returns {Function}
   * @memberof actions.rentalTicket
   * @example
   * 
   * // Clear RentalTicket
   * store.dispatch(actions.rentalTicket.rentalTicketState.unset());
   * 
   * // Clear RentalTicketDate
   * store.dispatch(actions.rentalTicket.rentalTicketState.unset('date'));
   * 
   * // Clear RentalTicketTime
   * store.dispatch(actions.rentalTicket.rentalTicketState.unset('time'));
   * 
   */
  unset(type = null) {
    return dispatch => {
      if (types(type).isString) {
        if (type === 'date') {
          dispatch(clearRentalTicketDate());
        } else if (type === 'time') {
          dispatch(clearRentalTicketTime());
        }
      } else {
        dispatch(clearRentalTicket());
      }
    };
  }
};


/**
 * Initiate rental ticket
 * @returns {Function}
 * @example
 * store.dispatch(actions.rentalTicket.initiateRental());
 */
export function initiateRental() {
  return (dispatch, getState) => {
    const state = getState();
    const payload = state.getIn(INITIATE).toJS();

    return dispatch(
      request({
        service: rentalTicket.modifyInitiate,
        data: { payload }
      })
    );
  };
}

/**
 * @private
 * @desc gets data from rental to be saved to UI state
 * @param {Immutable} session
 * @returns {Object}
 */
function getRentalTicketState(session) {
  const rental = session.getIn(['rental'])?.toJS();

  const location = {
    return_location: {
      date_time: rental?.ticket?.return_time,
      id: rental?.ticket?.return_location?.id,
    }
  }

  return location;
}

/**
 * @name hydrateUiRentalTicketState
 * @desc hydrates gmi.ui source of truth state from session
 * @param {Object} [opts] {initiateFromGma:Boolean, actionSession:Object}
 * @returns {Function}
 * @memberof actions.rentalTicket
 * @example
 * dispatch(actions.rentalTicket.hydrateUiRentalTicketState());
 * // will populate state.gmi.ui.rentalTicket.initiate
 */
export function hydrateUiRentalTicketState(opts = {}) {
  const { actionSession } = opts;
  return (dispatch, getState) => {
    const state = getState();
    const session =  fromJS(actionSession) || state.getIn(STATE.SESSION);
    const rentalState = getRentalTicketState(session);

    if(rentalState) return dispatch(setUiRentalTicket({ rentalState }));
  };
}